import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { toast } from 'sonner';
import { Link } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { useTheme } from '@/lib/contexts/theme-context';
import { DataTable } from '@/components/ui/data-table';
import { useBalance } from '@/lib/contexts/balance-context';
import { useAuth } from '@/lib/contexts/auth-context';
import { useApiCache } from '@/lib/contexts/api-cache-context';
import { usePermissions } from '@/lib/hooks/use-permissions';
import { Input } from '@/components/ui/input';
import { Checkbox } from '@/components/ui/checkbox';
import { Field, FieldLabel } from '@/components/ui/field';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import {
    // Plus,
    // Minus,
    Circle,
    Info,
    FileText,
    Mail,
    CreditCard,
    Building2,
    ChevronDown,
    ArrowLeft,
    ShoppingCart,
    Camera,
    ArrowUpFromLine,
    ArrowDownToLine,
    Copy,
    Search,
} from 'lucide-react';
import SheetTokensFaq from './_partials/sheets/SheetTokensFaq';
import SheetBilling from './_partials/sheets/SheetBilling';
import DialogContact from './_partials/dialogs/DialogContact';
import DialogTokensHistoryExport from './_partials/dialogs/DialogTokensHistoryExport';
import { horodatyApi } from '@/lib/utils/horodaty-api';
import { Skeleton } from '@/components/ui/skeleton';
import { useTranslation } from 'react-i18next';

const formatDate = (iso) => {
    if (!iso) return '—';
    try {
        return new Date(iso).toLocaleDateString('fr-FR');
    } catch {
        return '—';
    }
};

const formatTime = (iso) => {
    if (!iso) return '—';
    try {
        return new Date(iso).toLocaleTimeString('fr-FR');
    } catch {
        return '—';
    }
};

const parseCreditsFromProduct = (product) => {
    const credits = product?.metadata?.credits;
    if (credits !== undefined && credits !== null && credits !== '') {
        const qty = Number(credits);
        if (Number.isFinite(qty) && qty > 0) return qty;
    }

    const name = product?.name;
    if (!name) return null;
    const match = String(name).match(/(\d+)/);
    if (!match) return null;
    const qty = Number(match[1]);
    return Number.isFinite(qty) && qty > 0 ? qty : null;
};

const parseUnitAmount = (product) => {
    const raw = product?.default_price?.unit_amount;
    const num = Number(raw);
    return Number.isFinite(num) ? num : null;
};

/**
 * Returns the appropriate icon component based on transaction type
 */
const getTransactionIcon = (title, isCredit) => {
    const lowerTitle = (title || '').toLowerCase();

    if (lowerTitle.includes('achat')) {
        return ShoppingCart;
    }
    if (lowerTitle.includes('photo') || lowerTitle.includes('rapport')) {
        return Camera;
    }
    if (lowerTitle.includes('transfert') || lowerTitle.includes('envoi')) {
        return ArrowUpFromLine;
    }
    if (lowerTitle.includes('réception') || lowerTitle.includes('reçu')) {
        return ArrowDownToLine;
    }
    // Default based on credit/debit
    return isCredit ? ShoppingCart : Camera;
};

export default function Tokens() {
    const { t } = useTranslation();
    const { balance, refreshBalance } = useBalance();
    const { user, isLoading: isAuthLoading } = useAuth();
    // Détection particulier : flag company_is_fake OU rôle utilisateur 'user'
    // (cas legacy en BDD sans fake company). Note : /api/user sérialise `role` comme une string (nom du rôle).
    const isFakeCompany = user?.company_is_fake === true || user?.role === 'user';
    const { getCache, setCache } = useApiCache();
    const { can, canOnSelf, canOnDescendants } = usePermissions();
    const { theme } = useTheme();
    const isHorodateme = theme.slug === 'horodateme';
    const [activeTab, setActiveTab] = useState('purchase');
    const [quantities, setQuantities] = useState({});
    const [faqOpen, setFaqOpen] = useState(false);
    const [contactOpen, setContactOpen] = useState(false);
    const [billingOpen, setBillingOpen] = useState(false);
    const [exportOpen, setExportOpen] = useState(false);
    const [mobileStep, setMobileStep] = useState(1); // 1 = cart (token selection), 2 = order (billing/payment)

    const [isLoadingOffers, setIsLoadingOffers] = useState(false);
    const [offersError, setOffersError] = useState('');
    const [tokenOffers, setTokenOffers] = useState([]);

    const [isLoadingHistory, setIsLoadingHistory] = useState(false);
    const [historyError, setHistoryError] = useState('');
    const [historyData, setHistoryData] = useState([]);
    const [historyPage, setHistoryPage] = useState(1);
    const [historyPageSize, setHistoryPageSize] = useState(10);
    const [historyPageCount, setHistoryPageCount] = useState(1);
    const [historyTotal, setHistoryTotal] = useState(0);
    const [historySearch, setHistorySearch] = useState('');
    const [historySearchInput, setHistorySearchInput] = useState('');
    const historySearchTimeout = useRef(null);

    const [isCheckoutLoading, setIsCheckoutLoading] = useState(false);

    // Payment availability from admin_parameters
    const [paymentAvailability, setPaymentAvailability] = useState({ card: true, wire: true });
    useEffect(() => {
        horodatyApi.adminParameters
            .get()
            .then((res) => {
                const params = res?.data?.data?.company;
                if (!params) return;
                const cardEnabled = params.card_payment_enabled !== false;
                const wireEnabled = params.wire_transfer_enabled !== false;
                setPaymentAvailability({ card: cardEnabled, wire: wireEnabled });
                // Adjust default payment method if the current one is disabled
                setPaymentMethod((prev) => {
                    if (prev === 'card' && !cardEnabled) return wireEnabled ? 'transfer' : 'card';
                    if (prev === 'transfer' && !wireEnabled) return cardEnabled ? 'card' : 'transfer';
                    return prev;
                });
            })
            .catch(() => {}); // non-admin users or no params: keep defaults
    }, []);

    // State for new features
    const [promoCode, setPromoCode] = useState('');
    const [paymentMethod, setPaymentMethod] = useState('card'); // 'card' | 'transfer'
    const [acceptCGV, setAcceptCGV] = useState(false);
    const [selectedCompany, setSelectedCompany] = useState('');
    const [companies, setCompanies] = useState([]);

    // Billing info
    const [billingInfo, setBillingInfo] = useState({
        fullName: '',
        address: '',
        email: '',
        phone: '',
    });

    // Charger les informations de facturation depuis l'API company
    useEffect(() => {
        const companyId = user?.company_uniq_id;
        if (!companyId) return;

        // Vérifier le cache
        const cacheKey = `company-${companyId}`;
        const cached = getCache(cacheKey);

        const applyCompanyData = (company) => {
            const companyName = company?.name ?? '';
            const street = company?.street ?? '';
            const postalCode = company?.cp ?? '';
            const city = company?.city ?? '';

            // Construire l'adresse complète
            const addressParts = [street, postalCode, city].filter(Boolean);
            const address = addressParts.join(', ');

            setBillingInfo({
                fullName: companyName,
                address: address,
                email: company?.email ?? '',
                phone: company?.phone ?? '',
            });
        };

        if (cached) {
            applyCompanyData(cached);
            return;
        }

        let isMounted = true;

        horodatyApi
            .getCompany(companyId)
            .then((res) => {
                const company = res?.data?.data;
                if (!isMounted || !company) return;

                applyCompanyData(company);
                setCache(cacheKey, company, 10 * 60 * 1000); // 10 min TTL
            })
            .catch((error) => {
                console.error('Failed to load company info:', error);
            });

        return () => {
            isMounted = false;
        };
    }, [user?.company_uniq_id, getCache, setCache]);

    // Vérifier si les informations de facturation sont complètes
    const isBillingComplete = Boolean(
        billingInfo.fullName?.trim() && billingInfo.address?.trim() && billingInfo.email?.trim(),
    );

    // Colonnes pour la DataTable d'historique de transactions
    const historyColumns = useMemo(
        () => [
            {
                accessorKey: 'type',
                header: t('tokens:type'),
                cell: ({ row }) => {
                    const isCredit = row.original.tokens > 0;
                    const IconComponent = getTransactionIcon(row.original.title, isCredit);
                    return (
                        <div
                            className={`w-10 h-10 rounded-full flex items-center justify-center ${
                                isCredit ? 'bg-success/15' : 'bg-destructive/15'
                            }`}
                        >
                            <IconComponent className={`h-5 w-5 ${isCredit ? 'text-secondary' : 'text-destructive'}`} />
                        </div>
                    );
                },
                meta: { className: 'w-[60px]', hideOnMobile: true },
            },
            {
                accessorKey: 'title',
                header: t('tokens:description'),
                cell: ({ row }) => (
                    <div>
                        <p className="text-sm text-foreground">{row.original.title}</p>
                        {row.original.subtitle && (
                            <p className="text-sm text-muted-foreground">{row.original.subtitle}</p>
                        )}
                    </div>
                ),
                meta: { hideOnMobile: true },
            },
            {
                accessorKey: 'userName',
                header: t('tokens:responsable'),
                cell: ({ row }) => (
                    <span className="text-sm text-muted-foreground">{row.original.userName || '—'}</span>
                ),
                meta: { hideOnMobile: true },
            },
            {
                accessorKey: 'folderName',
                header: t('tokens:dossier'),
                cell: ({ row }) => (
                    <span className="text-sm text-muted-foreground">{row.original.folderName || '—'}</span>
                ),
                meta: { hideOnMobile: true },
            },
            {
                accessorKey: 'date',
                header: t('tokens:date'),
                cell: ({ row }) => (
                    <span className="text-sm text-muted-foreground">
                        {row.original.date} - {row.original.time}
                    </span>
                ),
                meta: { hideOnMobile: true },
            },
            {
                accessorKey: 'tokens',
                header: () => <span className="text-right block">{t('tokens:montant')}</span>,
                cell: ({ row }) => {
                    const isCredit = row.original.tokens > 0;
                    return (
                        <span
                            className={`text-[15px] font-medium block text-right ${
                                isCredit ? 'text-secondary' : 'text-destructive'
                            }`}
                        >
                            {isCredit ? '+' : ''}
                            {row.original.tokens}
                        </span>
                    );
                },
                meta: { hideOnMobile: true, className: 'text-right' },
            },
        ],
        [t],
    );

    // Bank details (shown when transfer is selected)
    const bankDetails = {
        iban: 'FR76 1234 5678 9012 3456 7890 123',
        bic: 'BNPAFRPP',
        beneficiary: 'Horodaty SAS',
    };

    const handleCopyToClipboard = (text, label) => {
        navigator.clipboard.writeText(text).then(() => {
            toast.success(t('tokens:copiedToClipboard', { label }));
        });
    };

    // Charger la liste des entreprises (entreprise principale + sous-comptes)
    const canSendToSubsidiary = canOnDescendants('SEND_TOKENS');

    useEffect(() => {
        const companyId = user?.company_uniq_id;
        if (!companyId) return;

        const cacheKey = `companies-list-${companyId}`;
        const cached = getCache(cacheKey);
        if (cached) {
            setCompanies(cached);
            if (!selectedCompany) setSelectedCompany(cached[0]?.value ?? '');
            return;
        }

        let isMounted = true;

        (async () => {
            try {
                const [companyRes, subsRes] = await Promise.all([
                    horodatyApi.getCompany(companyId),
                    canSendToSubsidiary
                        ? horodatyApi.subsidiaries.list(companyId).catch(() => null)
                        : Promise.resolve(null),
                ]);

                if (!isMounted) return;

                const company = companyRes?.data?.data;
                const mainEntry = {
                    value: companyId,
                    label: company?.name || t('tokens:monEntreprise'),
                };

                const subList = (subsRes?.data?.data ?? subsRes?.data ?? [])
                    .filter((s) => s?.name)
                    .map((s) => ({
                        value: s.uniq_id ?? s.id,
                        label: s.name,
                    }));

                const list = [mainEntry, ...subList];
                setCompanies(list);
                if (!selectedCompany) setSelectedCompany(list[0]?.value ?? '');
                setCache(cacheKey, list, 10 * 60 * 1000);
            } catch {
                // fallback
            }
        })();

        return () => {
            isMounted = false;
        };
    }, [user?.company_uniq_id, canSendToSubsidiary, getCache]);

    useEffect(() => {
        let isMounted = true;

        const loadOffers = async () => {
            // Vérifier le cache
            const cacheKey = 'token-offers';
            const cached = getCache(cacheKey);
            if (cached) {
                setTokenOffers(cached);
                return;
            }

            setIsLoadingOffers(true);
            setOffersError('');
            try {
                const res = await horodatyApi.stripeProducts();
                const products = res?.data?.data ?? [];
                const list = (Array.isArray(products) ? products : [])
                    .map((p) => {
                        const price = p?.default_price;
                        const unitAmount = parseUnitAmount(p);
                        const priceId = price?.id;
                        const qty = parseCreditsFromProduct(p);

                        if (!priceId || !unitAmount || !qty) return null;

                        const priceEuros = Number(unitAmount);
                        const pricePerToken =
                            p?.price_per_token !== undefined && p?.price_per_token !== null
                                ? Number(p.price_per_token)
                                : qty > 0
                                  ? priceEuros / qty
                                  : null;

                        return {
                            id: String(priceId),
                            priceId: String(priceId),
                            productId: String(p?.id || ''),
                            name: p?.name || `Pack ${qty} jetons`,
                            quantity: qty,
                            price: priceEuros,
                            pricePerToken: pricePerToken ?? null,
                            discount: null,
                            unitAmount: unitAmount,
                        };
                    })
                    .filter(Boolean)
                    .sort((a, b) => (a.unitAmount || 0) - (b.unitAmount || 0));

                if (!isMounted) return;
                setTokenOffers(list);
                setCache(cacheKey, list, 10 * 60 * 1000); // 10 min TTL
            } catch (error) {
                const message = error?.response?.data?.message || t('tokens:impossibleDeChargerLesOffresDeJetons');
                if (!isMounted) return;
                setOffersError(message);
                setTokenOffers([]);
            } finally {
                if (!isMounted) return;
                setIsLoadingOffers(false);
            }
        };

        loadOffers();

        return () => {
            isMounted = false;
        };
    }, [getCache, setCache]);

    const mapTransactions = useCallback((data) => {
        return (Array.isArray(data) ? data : []).map((t) => {
            const isCredit = t?.type === 'credit';
            const amount = Number(t?.token_amount ?? 0);
            const signedAmount = isCredit ? amount : -amount;
            return {
                id: String(t?.uniq_id ?? t?.id ?? Math.random()),
                tokens: signedAmount,
                title: t?.message || (isCredit ? t('tokens:credit') : t('tokens:debit')),
                date: formatDate(t?.created_at),
                time: formatTime(t?.created_at),
                userName: t?.user_name || null,
                folderName: t?.folder_name || null,
            };
        });
    }, [t]);

    useEffect(() => {
        if (activeTab !== 'history') return;
        if (isAuthLoading) return;

        let isMounted = true;
        setIsLoadingHistory(true);
        setHistoryError('');

        horodatyApi
            .getCompanyTransactions(user?.company_uniq_id, {
                paginate: true,
                page: historyPage,
                per_page: historyPageSize,
                ...(historySearch ? { search: historySearch } : {}),
            })
            .then((res) => {
                if (!isMounted) return;
                const data = res?.data?.data ?? [];
                const meta = res?.data?.meta;
                setHistoryData(mapTransactions(data));
                if (meta) {
                    setHistoryPageCount(meta.last_page ?? 1);
                    setHistoryTotal(meta.total ?? 0);
                }
            })
            .catch((error) => {
                const message = error?.response?.data?.message || t('tokens:impossibleDeChargerLHistorique');
                if (!isMounted) return;
                setHistoryError(message);
                setHistoryData([]);
            })
            .finally(() => {
                if (!isMounted) return;
                setIsLoadingHistory(false);
            });

        return () => {
            isMounted = false;
        };
    }, [activeTab, isAuthLoading, user, historyPage, historyPageSize, historySearch, mapTransactions]);

    const handleHistorySearchChange = useCallback((value) => {
        setHistorySearchInput(value);
        if (historySearchTimeout.current) clearTimeout(historySearchTimeout.current);
        historySearchTimeout.current = setTimeout(() => {
            setHistorySearch(value);
            setHistoryPage(1);
        }, 400);
    }, []);

    const getQuantity = (offerId) => quantities[offerId] || 0;

    // -- Ancien fonctionnement +/- (conservé si on veut permettre plusieurs fois la même ligne) --
    // const updateQuantity = (offerId, delta) => {
    //     setQuantities((prev) => {
    //         const current = prev[offerId] || 0;
    //         const nextValue = Math.max(0, current + delta);
    //
    //         if (nextValue <= 0) {
    //             const { [offerId]: _, ...rest } = prev;
    //             return rest;
    //         }
    //
    //         return { [offerId]: 1 };
    //     });
    // };
    //
    // const setQuantityValue = (offerId, value) => {
    //     const numValue = parseInt(value, 10);
    //     if (isNaN(numValue) || numValue < 0) return;
    //     setQuantities((prev) => {
    //         if (numValue === 0) {
    //             const { [offerId]: _, ...rest } = prev;
    //             return rest;
    //         }
    //         return { [offerId]: 1 };
    //     });
    // };
    // -- Fin ancien fonctionnement --

    const selectOffer = (offerId) => {
        setQuantities((prev) => (prev[offerId] ? {} : { [offerId]: 1 }));
    };

    // Compat : ancien nom utilisé ailleurs
    const updateQuantity = (offerId) => selectOffer(offerId);
    const setQuantityValue = (offerId, value) => {
        const numValue = parseInt(value, 10);
        if (isNaN(numValue) || numValue < 0) return;
        if (numValue === 0) {
            setQuantities((prev) => {
                const { [offerId]: _, ...rest } = prev;
                return rest;
            });
        } else {
            setQuantities({ [offerId]: 1 });
        }
    };

    const cartSummary = useMemo(() => {
        let totalTokens = 0;
        let totalPrice = 0;
        Object.entries(quantities).forEach(([offerId, qty]) => {
            const offer = tokenOffers.find((o) => String(o.id) === String(offerId));
            if (offer && qty > 0) {
                totalTokens += offer.quantity * qty;
                totalPrice += offer.price * qty;
            }
        });
        return { totalTokens, totalPrice };
    }, [quantities, tokenOffers]);

    const hasItemsInCart = cartSummary.totalTokens > 0;

    const selectedOffer = useMemo(() => {
        const selectedId = Object.keys(quantities).find((k) => quantities[k] > 0);
        if (!selectedId) return null;
        return tokenOffers.find((o) => String(o.id) === String(selectedId)) || null;
    }, [quantities, tokenOffers]);

    const handleCheckout = async () => {
        if (!acceptCGV) {
            toast.error(t('tokens:veuillezAccepterLesConditionsGeneralesDeVente'));
            return;
        }
        if (!isBillingComplete) {
            toast.error(t('tokens:veuillezRenseignerVosInformationsDeFacturation'));
            setBillingOpen(true);
            return;
        }
        if (!selectedOffer?.priceId) return;
        setIsCheckoutLoading(true);
        try {
            const res = await horodatyApi.stripeCheckoutUrl({
                price_id: selectedOffer.priceId,
                ...(selectedCompany ? { company_uniq_id: selectedCompany } : {}),
            });
            const url = res?.data?.checkout_url;
            if (url) {
                const width = 600;
                const height = 700;
                const left = (window.innerWidth - width) / 2 + window.screenX;
                const top = (window.innerHeight - height) / 2 + window.screenY;
                const popup = window.open(
                    url,
                    'stripe-checkout',
                    `width=${width},height=${height},left=${left},top=${top},scrollbars=yes`,
                );

                if (popup) {
                    const checkClosed = setInterval(() => {
                        if (popup.closed) {
                            clearInterval(checkClosed);
                            setIsCheckoutLoading(false);
                            refreshBalance();
                            setQuantities({});
                            const targetCompany = companies.find((c) => c.value === selectedCompany);
                            if (selectedCompany && selectedCompany !== user?.company_uniq_id && targetCompany) {
                                toast.success(t('tokens:paymentProcessedFor', { company: targetCompany.label }));
                            } else {
                                toast.success(t('tokens:paiementTraiteVotreSoldeAEteMisA'));
                            }
                        }
                    }, 500);
                } else {
                    toast.error(t('tokens:popupBloqueeVeuillezAutoriserLesPopupsPourCe'));
                    setIsCheckoutLoading(false);
                }
            }
        } catch {
            toast.error(t('tokens:erreurLorsDeLaCreationDuPaiement'));
            setIsCheckoutLoading(false);
        }
    };

    const handleApplyPromoCode = () => {
        if (promoCode.trim().length === 0) return;
        toast.error(t('tokens:codePromoNonValideOuExpire'));
    };

    const handleSaveBilling = (newBillingInfo) => {
        setBillingInfo(newBillingInfo);
        toast.success(t('tokens:informationsDeFacturationMisesAJour'));
    };

    // Side Panel Component (Desktop only)
    const sidePanel = (
        <div className="desktop-only hidden md:block md:w-[400px] md:border-r md:border-border md:bg-card md:overflow-y-auto">
            <div className="p-5 space-y-6">
                {/* Balance Card */}
                <div
                    id="desktop-tokens-card-balance"
                    className="bg-dark rounded-xl px-5 py-4 flex items-center justify-between"
                >
                    <div>
                        <span className="text-sm text-dark-foreground block">
                            {isFakeCompany ? t('tokens:soldeDesJetons') : t('tokens:soldeDeLEntreprise')}
                        </span>
                        <Link
                            to="#"
                            onClick={() => setActiveTab('history')}
                            className="text-xs text-secondary hover:underline mt-1 inline-block"
                        >
                            {t('tokens:voirLHistorique')}
                        </Link>
                    </div>
                    <span className="text-3xl text-secondary font-semibold">{balance ?? '—'}</span>
                </div>

                {/* Billing Section */}
                <div>
                    <h3 className="text-sm text-muted-foreground mb-3">{t('tokens:facturation')}</h3>
                    <Button
                        variant="soft"
                        color="neutral"
                        size="auto"
                        onClick={() => setBillingOpen(true)}
                        className="w-full items-start justify-between h-auto border border-border rounded-xl p-4 text-left"
                    >
                        <div className="flex-1">
                            <p className="text-[15px] text-foreground mb-1">{billingInfo.fullName}</p>
                            <p className="text-xs text-muted-foreground leading-relaxed">
                                {billingInfo.address}
                                <br />
                                {billingInfo.email}
                                <br />
                                {billingInfo.phone}
                            </p>
                        </div>
                        <span className="text-sm text-secondary">{t('tokens:modifier')}</span>
                    </Button>
                </div>

                {/* Promo Code Section */}
                <div>
                    <h3 className="text-sm text-muted-foreground mb-3">{t('tokens:codeParrainagePromotion')}</h3>
                    <div className="flex gap-3">
                        <Input
                            type="text"
                            maxLength={6}
                            placeholder="XXXXXX"
                            value={promoCode}
                            onChange={(e) => setPromoCode(e.target.value.toUpperCase())}
                            autoComplete="off"
                            className="flex-1 rounded-xl"
                        />
                        <Button
                            variant="solid"
                            color="secondary"
                            onClick={handleApplyPromoCode}
                            disabled={promoCode.trim().length === 0}
                            className="rounded-xl px-5"
                        >
                            {t('tokens:appliquer')}
                        </Button>
                    </div>
                </div>

                {/* Payment Method Section */}
                <div>
                    <h3 className="text-sm text-muted-foreground mb-3">{t('tokens:moyenDePaiement')}</h3>
                    <div
                        className={`grid gap-3 mb-3 ${isHorodateme || !paymentAvailability.wire ? 'grid-cols-1' : paymentAvailability.card ? 'grid-cols-2' : 'grid-cols-1'}`}
                    >
                        {paymentAvailability.card && (
                            <Button
                                variant="plain"
                                color="neutral"
                                onClick={() => setPaymentMethod('card')}
                                className={`h-auto bg-card border-2 rounded-xl p-3 flex flex-col justify-start gap-2 transition-colors hover:bg-muted/50 ${
                                    paymentMethod === 'card' ? 'border-secondary' : 'border-border'
                                }`}
                            >
                                <CreditCard className="h-5 w-5 text-foreground" />
                                <p className="text-sm text-foreground whitespace-normal">{t('tokens:carteBancaire')}</p>
                            </Button>
                        )}
                        {!isHorodateme && paymentAvailability.wire && (
                            <Button
                                variant="plain"
                                color="neutral"
                                onClick={() => setPaymentMethod('transfer')}
                                className={`h-auto bg-card border-2 rounded-xl p-3 flex flex-col justify-start gap-2 transition-colors hover:bg-muted/50 ${
                                    paymentMethod === 'transfer' ? 'border-secondary' : 'border-border'
                                }`}
                            >
                                <Building2 className="h-5 w-5 text-foreground" />
                                <p className="text-sm text-foreground whitespace-normal">{t('tokens:virementBancaire')}</p>
                            </Button>
                        )}
                    </div>

                    {/* Bank Details (shown when transfer is selected) */}
                    {!isHorodateme && paymentMethod === 'transfer' && (
                        <div className="bg-muted rounded-[12px] p-[16px] mb-4 border border-border">
                            <h4 className="text-[13px] text-foreground mb-[12px]">{t('tokens:coordonneesBancaires')}</h4>
                            <div className="space-y-[8px]">
                                <div className="flex items-center justify-between gap-[8px]">
                                    <div className="flex-1 min-w-0">
                                        <p className="text-[10px] text-muted-foreground">IBAN</p>
                                        <p className="text-[12px] text-foreground truncate">{bankDetails.iban}</p>
                                    </div>
                                    <Button
                                        variant="plain"
                                        color="neutral"
                                        size="auto"
                                        onClick={() => handleCopyToClipboard(bankDetails.iban, 'IBAN')}
                                        className="shrink-0"
                                    >
                                        <Copy className="h-4 w-4 text-muted-foreground" />
                                    </Button>
                                </div>
                                <div className="flex items-center justify-between gap-[8px]">
                                    <div className="flex-1 min-w-0">
                                        <p className="text-[10px] text-muted-foreground">BIC</p>
                                        <p className="text-[12px] text-foreground">{bankDetails.bic}</p>
                                    </div>
                                    <Button
                                        variant="plain"
                                        color="neutral"
                                        size="auto"
                                        onClick={() => handleCopyToClipboard(bankDetails.bic, 'BIC')}
                                        className="shrink-0"
                                    >
                                        <Copy className="h-4 w-4 text-muted-foreground" />
                                    </Button>
                                </div>
                                <div className="flex items-center justify-between gap-[8px]">
                                    <div className="flex-1 min-w-0">
                                        <p className="text-[10px] text-muted-foreground">{t('tokens:beneficiaire')}</p>
                                        <p className="text-[12px] text-foreground">{bankDetails.beneficiary}</p>
                                    </div>
                                    <Button
                                        variant="plain"
                                        color="neutral"
                                        size="auto"
                                        onClick={() => handleCopyToClipboard(bankDetails.beneficiary, t('tokens:beneficiaire'))}
                                        className="shrink-0"
                                    >
                                        <Copy className="h-4 w-4 text-muted-foreground" />
                                    </Button>
                                </div>
                            </div>
                        </div>
                    )}

                    <p className="text-[10px] text-muted-foreground leading-relaxed">
                        {isHorodateme
                            ? t('tokens:apresLaValidationDeVotreCommandeVousSerez')
                            : t('tokens:apresLaValidationDeVotreCommandeVousSerez2')}
                    </p>
                </div>
            </div>
        </div>
    );

    // Token Offer Item Component
    // -- Ancien TokenOfferItem avec boutons +/- (conservé si on veut permettre plusieurs fois la même ligne) --
    // const TokenOfferItem = ({ offer, qty, discountPercent, onAdd, onRemove }) => (
    //     <div className="px-5 py-4 border-b border-border last:border-b-0">
    //         <div className="flex items-center justify-between gap-3">
    //             <div className="flex-1">
    //                 <div className="flex items-baseline gap-2 mb-0.5">
    //                     <h3 className="text-lg text-foreground leading-tight">
    //                         {offer.quantity} jeton{offer.quantity > 1 ? 's' : ''}
    //                     </h3>
    //                     {discountPercent > 0 && (
    //                         <span className="bg-badge-discount rounded px-1.5 py-0.5 text-[11px] text-badge-discount-foreground">
    //                             -{discountPercent}%
    //                         </span>
    //                     )}
    //                 </div>
    //                 <div className="flex items-baseline gap-2">
    //                     <p className="text-xl text-secondary leading-tight">{offer.price.toFixed(2)}€</p>
    //                     {offer.pricePerToken !== null && (
    //                         <p className="text-xs text-muted-foreground leading-tight">
    //                             {offer.pricePerToken.toFixed(2)}€/jeton
    //                         </p>
    //                     )}
    //                 </div>
    //             </div>
    //             <div className="flex items-center">
    //                 {qty > 0 ? (
    //                     <div className="flex items-center gap-2">
    //                         <Button variant="solid" color="primary" onClick={onRemove} className="rounded-xl h-10 w-10 flex items-center justify-center">
    //                             <Minus className="h-5 w-5 text-primary-foreground" />
    //                         </Button>
    //                         <div className="bg-muted rounded-xl h-10 w-10 flex items-center justify-center">
    //                             <span className="text-base text-foreground">{qty}</span>
    //                         </div>
    //                         <Button variant="solid" color="primary" onClick={onAdd} disabled className="rounded-xl h-10 w-10 flex items-center justify-center">
    //                             <Plus className="h-5 w-5 text-primary-foreground" />
    //                         </Button>
    //                     </div>
    //                 ) : (
    //                     <Button variant="solid" color="primary" onClick={onAdd} className="rounded-xl h-10 w-10 flex items-center justify-center">
    //                         <Plus className="h-5 w-5 text-primary-foreground" />
    //                     </Button>
    //                 )}
    //             </div>
    //         </div>
    //     </div>
    // );
    // -- Fin ancien TokenOfferItem --

    const TokenOfferItem = ({ offer, selected, discountPercent, onSelect }) => (
        <div
            role="radio"
            aria-checked={selected}
            tabIndex={0}
            onClick={onSelect}
            onKeyDown={(e) => (e.key === ' ' || e.key === 'Enter') && onSelect()}
            className={`px-5 py-4 border-b border-border last:border-b-0 cursor-pointer transition-colors ${selected ? 'bg-muted/50' : 'hover:bg-muted/50'}`}
        >
            <div className="flex items-center justify-between gap-3">
                <div className="flex-1">
                    <div className="flex items-baseline gap-2 mb-0.5">
                        <h3 className="text-lg text-foreground leading-tight">
                            {offer.quantity} jeton{offer.quantity > 1 ? 's' : ''}
                        </h3>
                        {discountPercent > 0 && (
                            <span className="bg-badge-discount rounded px-1.5 py-0.5 text-[11px] text-badge-discount-foreground">
                                -{discountPercent}%
                            </span>
                        )}
                    </div>
                    <div className="flex items-baseline gap-2">
                        <p className="text-xl text-secondary leading-tight">{offer.price.toFixed(2)}€</p>
                        {offer.pricePerToken !== null && (
                            <p className="text-xs text-muted-foreground leading-tight">
                                {offer.pricePerToken.toFixed(2)}€/jeton
                            </p>
                        )}
                    </div>
                </div>
                <div className="relative flex items-center justify-center w-6 h-6">
                    <Circle className={`h-6 w-6 ${selected ? 'text-primary' : 'text-muted-foreground'}`} />
                    {selected && <Circle className="absolute h-3 w-3 text-primary" fill="currentColor" />}
                </div>
            </div>
        </div>
    );

    // Custom Offer Item Component
    const CustomOfferItem = () => (
        <div className="px-5 py-4 border-t border-border">
            <div className="flex items-center justify-between gap-3">
                <div className="flex-1">
                    <h3 className="text-lg text-foreground mb-0.5 leading-tight">{t('tokens:5000Jetons')}</h3>
                    <p className="text-xs text-muted-foreground leading-tight">{t('tokens:tarifSurMesure')}</p>
                </div>
                <Button
                    variant="solid"
                    color="primary"
                    onClick={() => setContactOpen(true)}
                    className="rounded-xl px-5 py-3 flex items-center justify-center gap-2 whitespace-nowrap"
                >
                    <Mail className="h-4 w-4 text-primary-foreground" />
                    <span className="text-sm text-primary-foreground">{t('tokens:contacter')}</span>
                </Button>
            </div>
        </div>
    );

    return (
        <div className="flex flex-col md:flex-row items-stretch size-full">
            {/* Side Panel - Desktop only */}
            {sidePanel}

            {/* Main Content */}
            <div className="flex-1 flex flex-col size-full overflow-hidden relative">
                {/* Mobile Header - only on mobile */}
                <div
                    id="mobile-tokens-header"
                    className="mobile-only md:hidden bg-dark px-5 py-3.5 flex items-center justify-between"
                >
                    {activeTab === 'purchase' ? (
                        <>
                            <div className="flex items-center gap-3">
                                <Button
                                    variant="plain"
                                    color="neutral"
                                    size="auto"
                                    onClick={() => {
                                        // Navigate back (could be browser history or app navigation)
                                        window.history.back();
                                    }}
                                    className="flex items-center justify-center"
                                >
                                    <ArrowLeft className="h-6 w-6 text-dark-foreground" />
                                </Button>
                                <div>
                                    <span className="text-sm text-dark-foreground block">
                                        {isFakeCompany
                                            ? t('tokens:soldeActuelDesJetons')
                                            : t('tokens:soldeActuelDeLEntreprise')}
                                    </span>
                                    <Button
                                        variant="link"
                                        color="secondary"
                                        onClick={() => setActiveTab('history')}
                                        className="h-auto p-0 text-[11px] hover:underline"
                                    >
                                        {t('tokens:voirLHistorique')}
                                    </Button>
                                </div>
                            </div>
                            <span className="text-2xl text-secondary font-semibold">{balance ?? '—'}</span>
                        </>
                    ) : (
                        <>
                            <div className="flex items-center gap-3">
                                <Button
                                    variant="plain"
                                    color="neutral"
                                    size="auto"
                                    onClick={() => setActiveTab('purchase')}
                                    className="flex items-center justify-center"
                                >
                                    <ArrowLeft className="h-6 w-6 text-primary-foreground" />
                                </Button>
                                <span className="text-sm text-primary-foreground">{t('tokens:historique')}</span>
                            </div>
                            {canOnSelf('VIEW_COMPANY_TOKENS') && (
                                <Button
                                    variant="solid"
                                    color="secondary"
                                    onClick={() => setExportOpen(true)}
                                    className="rounded-lg px-4 py-2 text-sm text-secondary-foreground transition-colors"
                                >
                                    {t('tokens:exporter')}
                                </Button>
                            )}
                        </>
                    )}
                </div>

                {/* Desktop Header - changes based on active tab */}
                <div className="desktop-only hidden md:block bg-card border-b border-border">
                    <div className="w-full flex items-center justify-between gap-3 px-5 py-4">
                        {activeTab === 'purchase' ? (
                            <>
                                <h2 className="text-xl text-foreground font-medium">{t('tokens:achatDeJetons')}</h2>
                                <Button
                                    variant="outline"
                                    color="neutral"
                                    size="auto"
                                    onClick={() => setFaqOpen(true)}
                                    aria-label={t('tokens:questionsFrequentes')}
                                    className="h-10 w-10 rounded-lg"
                                >
                                    <Info className="w-6! h-6! text-muted-foreground" />
                                </Button>
                            </>
                        ) : (
                            <>
                                <div className="flex items-center gap-3">
                                    <Button
                                        variant="plain"
                                        color="neutral"
                                        size="auto"
                                        onClick={() => setActiveTab('purchase')}
                                        className="flex items-center justify-center shrink-0"
                                    >
                                        <ArrowLeft className="h-5 w-5 text-foreground" />
                                    </Button>
                                    <h2 className="text-xl text-foreground font-medium">{t('tokens:historiqueDesJetons')}</h2>
                                </div>
                                {canOnSelf('VIEW_COMPANY_TOKENS') && (
                                    <Button
                                        variant="plain"
                                        color="success"
                                        onClick={() => setExportOpen(true)}
                                        className="flex items-center gap-2"
                                    >
                                        <FileText className="h-5 w-5" />
                                        <span className="text-sm">{t('tokens:exporterLHistorique')}</span>
                                    </Button>
                                )}
                            </>
                        )}
                    </div>
                </div>

                {/* Mobile Stepper - only show on purchase tab */}
                {activeTab === 'purchase' && (
                    <div className="mobile-only md:hidden bg-card px-5 pt-4 pb-5">
                        <div className="flex items-center justify-center gap-3">
                            {/* Step 1 */}
                            <Button
                                variant="plain"
                                color="neutral"
                                className="flex items-center gap-2 h-auto p-0"
                                onClick={() => setMobileStep(1)}
                            >
                                <div
                                    className={`h-7 w-7 rounded-full flex items-center justify-center text-[13px] font-medium ${
                                        mobileStep === 1
                                            ? 'bg-secondary text-primary-foreground'
                                            : 'bg-border text-muted-foreground'
                                    }`}
                                >
                                    1
                                </div>
                                <span
                                    className={`text-sm ${mobileStep === 1 ? 'text-foreground font-medium' : 'text-muted-foreground'}`}
                                >
                                    {t('tokens:panier')}
                                </span>
                            </Button>

                            {/* Connector */}
                            <div className="h-0.5 w-10 bg-border" />

                            {/* Step 2 */}
                            <Button
                                variant="plain"
                                color="neutral"
                                className="flex items-center gap-2 h-auto p-0"
                                onClick={() => setMobileStep(2)}
                            >
                                <div
                                    className={`h-7 w-7 rounded-full flex items-center justify-center text-[13px] font-medium ${
                                        mobileStep === 2
                                            ? 'bg-secondary text-primary-foreground'
                                            : 'bg-border text-muted-foreground'
                                    }`}
                                >
                                    2
                                </div>
                                <span
                                    className={`text-sm ${mobileStep === 2 ? 'text-foreground font-medium' : 'text-muted-foreground'}`}
                                >
                                    {t('tokens:commande')}
                                </span>
                            </Button>
                        </div>
                    </div>
                )}

                {/* Scrollable content */}
                {/*<div className="flex-1 overflow-y-auto overscroll-contain pb-[calc(160px+var(--safe-area-inset-bottom))] md:pb-[180px]">*/}
                <div className="flex-1 overflow-y-auto overscroll-contain pb-[var(--safe-area-inset-bottom))]">
                    {activeTab === 'purchase' && (
                        <div className="pb-5">
                            {/* Mobile Step 1: Token offers list */}
                            <div className={`md:block ${mobileStep === 1 ? 'block' : 'hidden'}`}>
                                {offersError ? (
                                    <div className="px-5 py-4 text-sm font-semibold text-destructive">
                                        {offersError}
                                    </div>
                                ) : null}

                                {isLoadingOffers ? (
                                    <div className="space-y-0">
                                        {Array.from({ length: 5 }).map((_, idx) => (
                                            <div key={idx} className="px-5 py-4 border-b border-border">
                                                <div className="flex items-center justify-between">
                                                    <div className="space-y-2">
                                                        <Skeleton className="h-5 w-24" />
                                                        <Skeleton className="h-6 w-16" />
                                                    </div>
                                                    <Skeleton className="w-10 h-10 rounded-xl" />
                                                </div>
                                            </div>
                                        ))}
                                    </div>
                                ) : tokenOffers.length === 0 ? (
                                    <div className="px-5 py-12 text-center">
                                        <p className="text-muted-foreground">{t('tokens:aucuneOffreDisponible')}</p>
                                    </div>
                                ) : (
                                    <div className="bg-card">
                                        {tokenOffers.map((offer, idx) => {
                                            const qty = getQuantity(offer.id);
                                            const referencePricePerToken = tokenOffers[0]?.pricePerToken;
                                            const currentPricePerToken = offer.pricePerToken;
                                            let discountPercent = 0;
                                            if (idx > 0 && referencePricePerToken && currentPricePerToken) {
                                                const discount =
                                                    ((referencePricePerToken - currentPricePerToken) /
                                                        referencePricePerToken) *
                                                    100;
                                                discountPercent = Math.round(discount);
                                            }
                                            return (
                                                <TokenOfferItem
                                                    key={offer.id}
                                                    offer={offer}
                                                    selected={qty > 0}
                                                    discountPercent={discountPercent}
                                                    onSelect={() => selectOffer(offer.id)}
                                                />
                                            );
                                        })}
                                        <CustomOfferItem />
                                    </div>
                                )}

                                {/* Info note - 3 lines */}
                                <p className="text-sm text-muted-foreground mt-4 leading-relaxed px-5">
                                    {t('tokens:1PhotographieHorodatee1Jeton')}
                                    <br />
                                    {t('tokens:1RapportGenere2Jetons')}
                                    <br />
                                    {t('tokens:pasDAbonnementNiDeFraisCaches')}
                                </p>
                            </div>

                            {/* Mobile Step 2: Promo code, billing info, payment method */}
                            <div className={`md:hidden ${mobileStep === 2 ? 'block' : 'hidden'}`}>
                                <div className="p-5 space-y-5">
                                    {/* Promo Code Section */}
                                    <div>
                                        <h3 className="text-sm text-muted-foreground mb-3">
                                            {t('tokens:codeParrainagePromotion')}
                                        </h3>
                                        <div className="bg-card rounded-xl p-4 border border-border">
                                            <div className="flex gap-3">
                                                <Input
                                                    type="text"
                                                    maxLength={6}
                                                    placeholder="XXXXXX"
                                                    value={promoCode}
                                                    onChange={(e) => setPromoCode(e.target.value)}
                                                    autoComplete="off"
                                                    className="flex-1 bg-muted rounded-[10px] px-4 h-11 text-[15px] border border-border placeholder:text-muted-foreground"
                                                />
                                                <Button
                                                    variant="solid"
                                                    color="secondary"
                                                    disabled={!promoCode.trim()}
                                                    className="rounded-[10px] px-5 h-11 text-[15px] disabled:opacity-50"
                                                    onClick={() => {
                                                        if (promoCode.trim()) {
                                                            toast.info(t('tokens:promoCodeApplied', { code: promoCode }));
                                                        }
                                                    }}
                                                >
                                                    {t('tokens:appliquer')}
                                                </Button>
                                            </div>
                                        </div>
                                    </div>

                                    {/* Billing Section */}
                                    <div>
                                        <h3 className="text-sm text-muted-foreground mb-3">{t('tokens:facturation')}</h3>
                                        <Button
                                            variant="plain"
                                            color="neutral"
                                            onClick={() => setBillingOpen(true)}
                                            className="w-full h-auto bg-card border border-border rounded-xl p-4 text-left transition-colors hover:bg-muted"
                                        >
                                            <div className="flex items-start justify-between">
                                                <div className="flex-1">
                                                    <p className="text-[15px] text-foreground mb-1">
                                                        {billingInfo.fullName}
                                                    </p>
                                                    <p className="text-xs text-muted-foreground leading-relaxed">
                                                        {billingInfo.address}
                                                        <br />
                                                        {billingInfo.email}
                                                        <br />
                                                        {billingInfo.phone}
                                                    </p>
                                                </div>
                                                <span className="text-[13px] text-secondary">{t('tokens:modifier')}</span>
                                            </div>
                                        </Button>
                                    </div>

                                    {/* Payment Method Section */}
                                    <div>
                                        <h3 className="text-sm text-muted-foreground mb-3">{t('tokens:moyenDePaiement')}</h3>
                                        <div
                                            className={`grid gap-3 mb-3 ${isHorodateme || !paymentAvailability.wire ? 'grid-cols-1' : paymentAvailability.card ? 'grid-cols-2' : 'grid-cols-1'}`}
                                        >
                                            {/* Card Payment Option */}
                                            {paymentAvailability.card && (
                                                <Button
                                                    variant="plain"
                                                    color="neutral"
                                                    onClick={() => setPaymentMethod('card')}
                                                    className={`h-auto bg-card border-2 rounded-xl p-3 flex flex-col justify-start gap-2 transition-colors ${
                                                        paymentMethod === 'card' ? 'border-secondary' : 'border-border'
                                                    }`}
                                                >
                                                    <CreditCard className="h-5 w-5 text-foreground" />
                                                    <p className="text-sm text-foreground whitespace-normal">
                                                        {t('tokens:carteBancaire')}
                                                    </p>
                                                </Button>
                                            )}

                                            {/* Bank Transfer Option */}
                                            {!isHorodateme && paymentAvailability.wire && (
                                                <Button
                                                    variant="plain"
                                                    color="neutral"
                                                    onClick={() => setPaymentMethod('transfer')}
                                                    className={`h-auto bg-card border-2 rounded-xl p-3 flex flex-col justify-start gap-2 transition-colors ${
                                                        paymentMethod === 'transfer'
                                                            ? 'border-secondary'
                                                            : 'border-border'
                                                    }`}
                                                >
                                                    <Building2 className="h-5 w-5 text-foreground" />
                                                    <p className="text-sm text-foreground whitespace-normal">
                                                        {t('tokens:virementBancaire')}
                                                    </p>
                                                </Button>
                                            )}
                                        </div>

                                        {/* Bank details shown when transfer is selected */}
                                        {!isHorodateme && paymentMethod === 'transfer' && (
                                            <div className="bg-muted rounded-[12px] p-[16px] border border-border">
                                                <h4 className="text-[14px] text-foreground mb-[12px]">
                                                    {t('tokens:coordonneesBancaires')}
                                                </h4>
                                                <div className="space-y-[10px]">
                                                    <div className="flex items-center justify-between gap-[8px]">
                                                        <div className="flex-1">
                                                            <p className="text-[11px] text-muted-foreground">IBAN</p>
                                                            <p className="text-[13px] text-foreground">
                                                                {bankDetails.iban}
                                                            </p>
                                                        </div>
                                                        <Button
                                                            variant="plain"
                                                            color="neutral"
                                                            size="auto"
                                                            onClick={() =>
                                                                handleCopyToClipboard(bankDetails.iban, 'IBAN')
                                                            }
                                                            className="shrink-0"
                                                        >
                                                            <Copy className="h-5 w-5 text-muted-foreground" />
                                                        </Button>
                                                    </div>
                                                    <div className="flex items-center justify-between gap-[8px]">
                                                        <div className="flex-1">
                                                            <p className="text-[11px] text-muted-foreground">BIC</p>
                                                            <p className="text-[13px] text-foreground">
                                                                {bankDetails.bic}
                                                            </p>
                                                        </div>
                                                        <Button
                                                            variant="plain"
                                                            color="neutral"
                                                            size="auto"
                                                            onClick={() =>
                                                                handleCopyToClipboard(bankDetails.bic, 'BIC')
                                                            }
                                                            className="shrink-0"
                                                        >
                                                            <Copy className="h-5 w-5 text-muted-foreground" />
                                                        </Button>
                                                    </div>
                                                    <div className="flex items-center justify-between gap-[8px]">
                                                        <div className="flex-1">
                                                            <p className="text-[11px] text-muted-foreground">
                                                                {t('tokens:beneficiaire')}
                                                            </p>
                                                            <p className="text-[13px] text-foreground">
                                                                {bankDetails.beneficiary}
                                                            </p>
                                                        </div>
                                                        <Button
                                                            variant="plain"
                                                            color="neutral"
                                                            size="auto"
                                                            onClick={() =>
                                                                handleCopyToClipboard(
                                                                    bankDetails.beneficiary,
                                                                    t('tokens:beneficiaire'),
                                                                )
                                                            }
                                                            className="shrink-0"
                                                        >
                                                            <Copy className="h-5 w-5 text-muted-foreground" />
                                                        </Button>
                                                    </div>
                                                </div>
                                            </div>
                                        )}

                                        {/* Disclaimer */}
                                        <p className="text-[10px] text-muted-foreground leading-tight mt-4">
                                            {isHorodateme
                                                ? t('tokens:apresLaValidationDeVotreCommandeVousSerez')
                                                : t('tokens:apresLaValidationDeVotreCommandeVousSerez3')}
                                        </p>
                                    </div>
                                </div>
                            </div>
                        </div>
                    )}

                    {activeTab === 'history' && (
                        <div className="w-full">
                            {historyError ? (
                                <div className="p-5 text-sm font-semibold text-destructive">{historyError}</div>
                            ) : null}

                            {/* Search bar */}
                            <div className="px-5 py-3 border-b border-border bg-card">
                                <div className="relative">
                                    <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                                    <Input
                                        type="text"
                                        placeholder={t('tokens:rechercherUneTransaction')}
                                        value={historySearchInput}
                                        onChange={(e) => handleHistorySearchChange(e.target.value)}
                                        className="pl-9 rounded-xl"
                                    />
                                </div>
                            </div>

                            {isLoadingHistory ? (
                                <>
                                    {/* Desktop Loading */}
                                    <div className="hidden md:block bg-card border border-border overflow-hidden">
                                        <DataTable
                                            columns={historyColumns}
                                            data={[]}
                                            isLoading={true}
                                            emptyMessage={t('tokens:aucuneTransaction')}
                                            showPagination={false}
                                        />
                                    </div>
                                    {/* Mobile Loading */}
                                    <div className="md:hidden bg-card">
                                        {Array.from({ length: 6 }).map((_, idx) => (
                                            <div
                                                key={idx}
                                                className="px-5 py-4 flex items-center justify-between gap-4 border-b border-border"
                                            >
                                                <div className="flex-1 space-y-2">
                                                    <Skeleton className="h-5 w-24" />
                                                    <Skeleton className="h-4 w-48" />
                                                    <Skeleton className="h-3 w-32" />
                                                </div>
                                                <Skeleton className="h-10 w-10 rounded-xl shrink-0" />
                                            </div>
                                        ))}
                                    </div>
                                </>
                            ) : historyData.length === 0 ? (
                                <div className="bg-card rounded-lg border border-border px-6 py-12 text-center mx-5 mt-5">
                                    <p className="text-muted-foreground">{t('tokens:aucuneTransaction')}</p>
                                </div>
                            ) : (
                                <>
                                    {/* Desktop Table */}
                                    <div className="hidden md:block bg-card border border-border overflow-hidden">
                                        <DataTable
                                            columns={historyColumns}
                                            data={historyData}
                                            emptyMessage={t('tokens:aucuneTransaction')}
                                            showPagination={true}
                                            manualPagination={true}
                                            pageCount={historyPageCount}
                                            currentPage={historyPage}
                                            onPageChange={setHistoryPage}
                                            onPageSizeChange={(size) => {
                                                setHistoryPageSize(size);
                                                setHistoryPage(1);
                                            }}
                                            totalCount={historyTotal}
                                            pageSize={historyPageSize}
                                            isLoading={isLoadingHistory}
                                        />
                                    </div>

                                    {/* Mobile List */}
                                    <div className="md:hidden bg-card">
                                        {historyData.map((item) => {
                                            const isCredit = item.tokens > 0;
                                            const IconComponent = getTransactionIcon(item.title, isCredit);
                                            return (
                                                <div
                                                    key={item.id}
                                                    className="px-5 py-4 flex items-center justify-between gap-4 border-b border-border last:border-b-0"
                                                >
                                                    <div className="flex-1">
                                                        <h3
                                                            className={`text-lg font-medium mb-0.5 ${
                                                                isCredit ? 'text-secondary' : 'text-destructive'
                                                            }`}
                                                        >
                                                            {isCredit ? '+' : ''}
                                                            {item.tokens} jeton
                                                            {Math.abs(item.tokens) > 1 ? 's' : ''}
                                                        </h3>
                                                        <p className="text-sm text-foreground mb-0.5">{item.title}</p>
                                                        {item.subtitle && (
                                                            <p className="text-xs text-muted-foreground mb-0.5">
                                                                {item.subtitle}
                                                            </p>
                                                        )}
                                                        <p className="text-xs text-muted-foreground">
                                                            {item.date} - {item.time}
                                                        </p>
                                                    </div>
                                                    <div className="shrink-0">
                                                        <div
                                                            className={`rounded-xl h-10 w-10 flex items-center justify-center ${
                                                                isCredit ? 'bg-success/15' : 'bg-destructive/15'
                                                            }`}
                                                        >
                                                            <IconComponent
                                                                className={`h-5 w-5 ${
                                                                    isCredit ? 'text-secondary' : 'text-destructive'
                                                                }`}
                                                            />
                                                        </div>
                                                    </div>
                                                </div>
                                            );
                                        })}
                                        {/* Mobile pagination */}
                                        {historyPageCount > 1 && (
                                            <div className="flex items-center justify-center gap-4 py-4 border-t border-border">
                                                <Button
                                                    variant="outline"
                                                    color="neutral"
                                                    size="auto"
                                                    className="size-8"
                                                    onClick={() => setHistoryPage((p) => Math.max(1, p - 1))}
                                                    disabled={historyPage <= 1}
                                                >
                                                    <ArrowLeft className="h-4 w-4" />
                                                </Button>
                                                <span className="text-sm text-muted-foreground">
                                                    {t('tokens:page')} {historyPage}/{historyPageCount}
                                                </span>
                                                <Button
                                                    variant="outline"
                                                    color="neutral"
                                                    size="auto"
                                                    className="size-8"
                                                    onClick={() =>
                                                        setHistoryPage((p) => Math.min(historyPageCount, p + 1))
                                                    }
                                                    disabled={historyPage >= historyPageCount}
                                                >
                                                    <ArrowLeft className="h-4 w-4 rotate-180" />
                                                </Button>
                                            </div>
                                        )}
                                    </div>
                                </>
                            )}
                        </div>
                    )}
                </div>

                {/* Mobile Footer */}
                {hasItemsInCart && activeTab === 'purchase' && (
                    <div className="mobile-only md:hidden fixed bottom-0 left-0 right-0 bg-card z-45 border-t border-border px-5 pt-4 pb-[calc(16px+env(safe-area-inset-bottom,0px))] shadow-[0_-2px_10px_rgba(0,0,0,0.1)]">
                        {mobileStep === 1 ? (
                            /* Step 1: Show total and "Continuer" button */
                            <>
                                <div className="flex items-center justify-between mb-4">
                                    <div>
                                        <p className="text-xl text-foreground font-medium">
                                            {cartSummary.totalTokens} jetons
                                        </p>
                                        <p className="text-xl text-secondary">{cartSummary.totalPrice.toFixed(2)}€</p>
                                    </div>
                                </div>
                                <Button
                                    onClick={() => setMobileStep(2)}
                                    className="bg-primary rounded-xl w-full py-3.5 hover:bg-primary/90 transition-colors"
                                >
                                    <span className="text-base text-primary-foreground">{t('tokens:continuer')}</span>
                                </Button>
                            </>
                        ) : (
                            /* Step 2: Show total, company select, CGV, and "Valider la commande" button */
                            <>
                                <div className="flex items-center justify-between mb-2.5">
                                    <div>
                                        <p className="text-xl text-foreground font-medium">
                                            {cartSummary.totalTokens} jetons
                                        </p>
                                        <p className="text-xl text-secondary">{cartSummary.totalPrice.toFixed(2)}€</p>
                                    </div>
                                    {companies.length > 1 && (
                                        <Select value={selectedCompany} onValueChange={setSelectedCompany}>
                                            <SelectTrigger className="bg-muted border border-border rounded-xl px-3 py-2 text-sm text-foreground h-auto max-w-[45%]">
                                                <SelectValue placeholder={t('tokens:entreprise')} />
                                            </SelectTrigger>
                                            <SelectContent>
                                                {companies.map((company) => (
                                                    <SelectItem key={company.value} value={company.value}>
                                                        {company.label}
                                                    </SelectItem>
                                                ))}
                                            </SelectContent>
                                        </Select>
                                    )}
                                </div>
                                <Field orientation="horizontal" className="mb-5">
                                    <Checkbox
                                        id="accept-cgv-mobile"
                                        checked={acceptCGV}
                                        onCheckedChange={setAcceptCGV}
                                        className="h-[18px] w-[18px] rounded-[5px]"
                                    />
                                    <FieldLabel
                                        htmlFor="accept-cgv-mobile"
                                        className="flex flex-nowrap text-xs text-secondary text-left leading-tight cursor-pointer gap-0"
                                    >
                                        <span className="shrink-0">{t('tokens:jAccepteLes')} </span>
                                        <a
                                            href="https://horodaty.fr/cgv/"
                                            target="_blank"
                                            rel="noopener noreferrer"
                                            onClick={(e) => e.stopPropagation()}
                                            className="shrink-1 underline hover:opacity-80"
                                        >
                                            {t('tokens:conditionsGeneralesDeVente')}
                                        </a>
                                    </FieldLabel>
                                </Field>
                                {!isBillingComplete && (
                                    <button
                                        onClick={() => setBillingOpen(true)}
                                        className="w-full text-[11px] text-destructive mb-4 text-center hover:underline transition-all"
                                    >
                                        {t('tokens:veuillezRenseignerVos')}{' '}
                                        <span className="font-semibold">{t('tokens:informationsDeFacturation')}</span> {t('tokens:pourCommander')}
                                    </button>
                                )}
                                <Button
                                    onClick={handleCheckout}
                                    disabled={!acceptCGV || !isBillingComplete || isCheckoutLoading}
                                    className="bg-primary rounded-xl w-full py-3.5 hover:bg-primary/90 transition-colors disabled:opacity-50"
                                >
                                    <span className="text-base text-primary-foreground">
                                        {isCheckoutLoading ? t('tokens:redirection') : t('tokens:validerLaCommande')}
                                    </span>
                                </Button>
                            </>
                        )}
                    </div>
                )}

                {/* Desktop Footer */}
                {hasItemsInCart && activeTab === 'purchase' && (
                    <div className="desktop-only hidden md:block fixed bottom-0 left-[400px] right-0 bg-card border-t border-border z-40">
                        <div className="px-5 py-5">
                            <div className="flex items-center justify-between mb-4">
                                <div>
                                    <p className="text-sm text-muted-foreground">{t('tokens:total')}</p>
                                    <p className="text-lg text-foreground font-medium">
                                        {cartSummary.totalTokens} jetons
                                    </p>
                                    <p className="text-2xl text-secondary">{cartSummary.totalPrice.toFixed(2)}€</p>
                                </div>
                                {companies.length > 1 && (
                                    <div>
                                        <span className="text-xs text-muted-foreground mb-1.5 block">
                                            {t('tokens:achetePour')}
                                        </span>
                                        <Select value={selectedCompany} onValueChange={setSelectedCompany}>
                                            <SelectTrigger className="bg-card border border-border rounded-xl px-4 py-2.5 text-sm text-foreground h-auto">
                                                <SelectValue placeholder={t('tokens:entreprise')} />
                                            </SelectTrigger>
                                            <SelectContent>
                                                {companies.map((company) => (
                                                    <SelectItem key={company.value} value={company.value}>
                                                        {company.label}
                                                    </SelectItem>
                                                ))}
                                            </SelectContent>
                                        </Select>
                                    </div>
                                )}
                            </div>
                            <div className="flex flex-col gap-4">
                                <Field orientation="horizontal">
                                    <Checkbox
                                        id="accept-cgv-desktop"
                                        checked={acceptCGV}
                                        onCheckedChange={setAcceptCGV}
                                        className="h-[18px] w-[18px] rounded-[5px]"
                                    />
                                    <FieldLabel
                                        htmlFor="accept-cgv-desktop"
                                        className="text-xs text-secondary cursor-pointer"
                                    >
                                        {t('tokens:jAccepteLes')}{' '}
                                        <a
                                            href="https://horodaty.fr/cgv/"
                                            target="_blank"
                                            rel="noopener noreferrer"
                                            onClick={(e) => e.stopPropagation()}
                                            className="underline hover:opacity-80"
                                        >
                                            {t('tokens:conditionsGeneralesDeVente')}
                                        </a>
                                    </FieldLabel>
                                </Field>
                                {!isBillingComplete && (
                                    <button
                                        onClick={() => setBillingOpen(true)}
                                        className="text-xs text-destructive text-center mb-1 hover:underline transition-all"
                                    >
                                        {t('tokens:veuillezRenseignerVos')}{' '}
                                        <span className="font-semibold">{t('tokens:informationsDeFacturation')}</span> {t('tokens:pourCommander')}
                                    </button>
                                )}
                                <Button
                                    onClick={handleCheckout}
                                    disabled={!acceptCGV || !isBillingComplete || isCheckoutLoading}
                                    className="bg-primary rounded-xl px-12 py-4 hover:bg-primary/90 transition-colors whitespace-nowrap w-full disabled:opacity-50"
                                >
                                    <span className="text-base text-primary-foreground">
                                        {isCheckoutLoading ? t('tokens:redirection') : t('tokens:validerLaCommande')}
                                    </span>
                                </Button>
                            </div>
                        </div>
                    </div>
                )}
            </div>

            {/* FAQ Sheet */}
            <SheetTokensFaq open={faqOpen} onClose={() => setFaqOpen(false)} />

            {/* Billing Sheet */}
            <SheetBilling
                open={billingOpen}
                onClose={() => setBillingOpen(false)}
                billingInfo={billingInfo}
                onSave={handleSaveBilling}
            />

            {/* Contact Dialog (was Drawer) */}
            <DialogContact open={contactOpen} onClose={() => setContactOpen(false)} />

            {/* Export History Dialog */}
            <DialogTokensHistoryExport
                open={exportOpen}
                onClose={() => setExportOpen(false)}
                companyUniqId={user?.company_uniq_id}
            />
        </div>
    );
}
